import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  cronPreset,
  cronValues,
  months,
  newCron,
  newInterval,
  weekdays,
} from "./scheduleHelpers";

const fieldSpecs = [
  ["minute", "Minutes", 0, 59, []],
  ["hour", "Hours", 0, 23, []],
  ["day_of_week", "Days of week", 0, 6, weekdays],
  ["day_of_month", "Days of month", 1, 31, []],
  ["month_of_year", "Months", 1, 12, months],
];
const optionsByField = Object.fromEntries(
  fieldSpecs.map(([field, , min, max, names]) => [
    field,
    Array.from({ length: max - min + 1 }, (_, index) => ({
      value: min + index,
      label: names[index] || String(min + index).padStart(2, "0"),
    })),
  ])
);

export default function ScheduleFields({ schedule, onChange, timezones }) {
  const [preset, setPreset] = useState(
    schedule.type === "cron" ? cronPreset(schedule) : "daily"
  );
  const change = (values) => onChange({ ...schedule, ...values });
  const choosePreset = (value) => {
    setPreset(value);
    if (value === "custom") return;
    change({
      minute: /^\d+$/.test(schedule.minute) ? schedule.minute : "0",
      hour: /^\d+$/.test(schedule.hour) ? schedule.hour : "9",
      month_of_year: "*",
      day_of_week: value === "weekly" ? "0,1,2,3,4" : "*",
      day_of_month: value === "monthly" ? "1" : "*",
    });
  };
  const selectedDays =
    schedule.type === "cron"
      ? cronValues(schedule.day_of_week, 0, 6, weekdays)
      : [];
  return (
    <Box className="schedule-fields">
      <TextField
        select
        SelectProps={{ native: true }}
        label="Schedule type"
        value={schedule.type}
        onChange={(event) => {
          setPreset("daily");
          onChange(
            event.target.value === "interval" ? newInterval() : newCron()
          );
        }}
      >
        <option value="interval">Repeat every…</option>
        <option value="cron">At specific times (cron)</option>
      </TextField>
      {schedule.type === "interval" ? (
        <>
          <Stack direction="row" spacing={2} className="schedule-field-full">
            <TextField
              fullWidth
              label="Every"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={schedule.every}
              onChange={(e) =>
                change({
                  every: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
            />
            <TextField
              fullWidth
              select
              SelectProps={{ native: true }}
              label="Unit"
              value={schedule.period}
              onChange={(e) => change({ period: e.target.value })}
            >
              {["seconds", "minutes", "hours", "days", "microseconds"].map(
                (unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                )
              )}
            </TextField>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            className="schedule-field-full"
          >
            Intervals repeat after the last scheduled run. To run at the same
            local time each day, choose a cron schedule.
          </Typography>
        </>
      ) : (
        <>
          <TextField
            select
            SelectProps={{ native: true }}
            label="Repeat"
            value={preset}
            onChange={(e) => choosePreset(e.target.value)}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly / selected weekdays</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom calendar</option>
          </TextField>
          {preset !== "custom" ? (
            <TextField
              label="Time"
              type="time"
              InputLabelProps={{ shrink: true }}
              value={`${schedule.hour.padStart(2, "0")}:${schedule.minute.padStart(2, "0")}`}
              onChange={(e) => {
                const [hour = "", minute = ""] = e.target.value.split(":");
                change({
                  hour: hour === "" ? "" : String(Number(hour)),
                  minute: minute === "" ? "" : String(Number(minute)),
                });
              }}
            />
          ) : null}
          {preset === "weekly" ? (
            <Box className="schedule-field-full schedule-weekdays">
              <Typography variant="body2">Run on</Typography>
              {weekdays.map((name, value) => (
                <FormControlLabel
                  key={name}
                  label={name.slice(0, 3)}
                  control={
                    <Checkbox
                      checked={selectedDays.includes(value)}
                      onChange={(_, checked) =>
                        change({
                          day_of_week: (checked
                            ? [...selectedDays, value]
                            : selectedDays.filter((day) => day !== value)
                          )
                            .sort()
                            .join(","),
                        })
                      }
                    />
                  }
                />
              ))}
              {!selectedDays.length ? (
                <Alert severity="warning">Choose at least one weekday.</Alert>
              ) : null}
            </Box>
          ) : null}
          {preset === "monthly" ? (
            <TextField
              select
              SelectProps={{ native: true }}
              label="Day of month"
              value={schedule.day_of_month}
              onChange={(e) => change({ day_of_month: e.target.value })}
              helperText="Months without the selected date are skipped."
            >
              {Array.from({ length: 31 }, (_, index) => (
                <option key={index} value={index + 1}>
                  {index + 1}
                </option>
              ))}
            </TextField>
          ) : null}
          {preset === "custom" ? (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                className="schedule-field-full"
              >
                Choose specific values below, or leave a field empty for every
                value. Selected weekdays and dates must both match.
              </Typography>
              {fieldSpecs.map(([field, label, min, max, names]) => {
                const selected = cronValues(schedule[field], min, max, names);
                const options = optionsByField[field];
                return (
                  <Autocomplete
                    componentsProps={{ popper: { sx: { zIndex: 1600 } } }}
                    key={field}
                    multiple
                    disableCloseOnSelect
                    size="small"
                    renderTags={(values, getTagProps) => (
                      <>
                        {values.slice(0, 2).map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.value}
                            label={option.label}
                            size="small"
                          />
                        ))}
                        {values.length > 2 ? (
                          <Typography variant="caption" sx={{ px: 0.5 }}>
                            +{values.length - 2} more
                          </Typography>
                        ) : null}
                      </>
                    )}
                    options={options}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.value}>
                        <Checkbox
                          checked={selected}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {option.label}
                      </li>
                    )}
                    value={options.filter((option) =>
                      selected.includes(option.value)
                    )}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    onChange={(_, values) =>
                      change({
                        [field]: values.length
                          ? values
                              .map((option) => option.value)
                              .sort((a, b) => a - b)
                              .join(",")
                          : "*",
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={label}
                        InputLabelProps={{ shrink: true }}
                        placeholder={
                          selected.length
                            ? ""
                            : {
                                minute: "Every minute",
                                hour: "Every hour",
                                day_of_week: "Every day",
                                day_of_month: "Every date",
                                month_of_year: "Every month",
                              }[field]
                        }
                      />
                    )}
                  />
                );
              })}
            </>
          ) : null}
          {preset === "custom" && schedule.minute === "*" ? (
            <Alert
              severity="info"
              className="schedule-field-full"
              sx={{ fontSize: 12 }}
            >
              Runs every minute during the selected hours. Select a minute, such
              as 00, to run once per hour.
            </Alert>
          ) : null}
          <Autocomplete
            className="schedule-field-full"
            componentsProps={{ popper: { sx: { zIndex: 1600 } } }}
            options={timezones}
            value={schedule.timezone}
            disableClearable
            onChange={(_, value) => change({ timezone: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                helperText="Run times use this timezone. Regional zones adjust for daylight saving; fixed zones such as EST do not."
              />
            )}
          />
        </>
      )}
    </Box>
  );
}
